/**
 * CanvasApplication - Main application class for CanvasApp_v2
 * Extends BaseApplication to provide canvas-specific functionality
 * 
 * This demonstrates how the unified architecture works for spatial apps!
 */

import * as Y from 'yjs';
import { BaseApplication } from '../../../services/baseApplication/BaseApplication';
import { AppConfig, SerializedNode } from '../../../services/baseApplication/types';
import { CANVAS_APP_CONFIG, getClassesByCategory } from './CanvasConfig';
import { entityService } from '../../../services/entityService';

export class CanvasApplication extends BaseApplication {
  // ✅ collaborationProvider inherited from BaseApplication
  public undoManager: Y.UndoManager | null = null;
  
  constructor() {
    super();
    // ✅ REQUIRED: Disable suggestion mode for Canvas
    // Insert/replace operations still check suggestionMode - keep false to avoid marking elements
    this.suggestionMode = false;
  }
  
  /**
   * Get application configuration
   */
  protected getConfig(): AppConfig {
    return CANVAS_APP_CONFIG;
  }
  
  /**
   * Load canvas from backend
   * ✅ REFACTORED: Self-contained with BaseApplication persistence
   * ✅ BUG FIX: Correct load sequence - create Y.Doc FIRST, then initialize
   */
  async load(entityId: string, options: { offline?: boolean } = {}): Promise<void> {
    console.log('[CanvasApplication] Loading canvas:', entityId);
    
    try {
      // 1. Fetch entity from backend
      const entity = await entityService.get(entityId);
      
      if (!entity) {
        throw new Error(`Canvas not found: ${entityId}`);
      }
      
      console.log('[CanvasApplication] Entity fetched:', {
        id: entity._id,
        type: entity.entityType,
        hasBase64State: !!entity.skeleton?.base64
      });
      
      // ✅ FIX: Set entityId FIRST (before creating Y.Doc)
      this.entityId = entityId;
      
      // ✅ FIX: Create Y.Doc BEFORE initialize()
      // This prevents initialize() from creating an empty Y.Doc that gets discarded
      console.log('[CanvasApplication] 📥 Creating self-contained Y.Doc...');
      this.ydoc = new Y.Doc();
      console.log('[CanvasApplication] 📥 Y.Doc created, clientID:', this.ydoc.clientID);
      
      // ✅ FIX: Load base64 state BEFORE getting fragment (with error handling)
      if (entity.skeleton?.base64) {
        console.log('[CanvasApplication] 📥 Loading base64 state...');
        try {
          this.loadBase64State(entity.skeleton.base64);
          console.log('[CanvasApplication] ✅ Base64 state loaded');
        } catch (error) {
          console.error('[CanvasApplication] ❌ Failed to load Y.js state:', error);
          this.emit('loadError', { 
            error, 
            message: 'Failed to load canvas data. Starting with empty canvas.' 
          });
          // Continue with empty canvas rather than failing completely
          console.log('[CanvasApplication] ⚠️ Continuing with empty canvas');
        }
      }
      
      // Get content fragment AFTER loading state
      this.yxml = this.ydoc.getXmlFragment('prosemirror');
      console.log('[CanvasApplication] 📋 Got prosemirror fragment, length:', this.yxml.length);
      
      // Create UndoManager for Y.js-tracked undo/redo
      this.undoManager = new Y.UndoManager([this.yxml], {
        trackedOrigins: new Set([this.ydoc.clientID]),
        captureTimeout: 300,
      });
      console.log('[CanvasApplication] ✅ UndoManager created');
      
      // Initialize empty if NO existing state
      if (this.yxml.length === 0 && !entity.skeleton?.base64) {
        this.initializeEmptyCanvas();
      }
      
      // ✅ FIX: Setup auto-save BEFORE building indices
      // This ensures migrations during index building get auto-saved
      this.setupAutoSave();
      console.log('[CanvasApplication] ✅ Auto-save enabled');
      
      // ✅ FIX: Build indices ONCE (with loaded content)
      await this.buildCoreIndices();
      
      // ✅ Optional: Setup WebSocket collaboration
      if (!options.offline) {
        await this.ensureCollaboration(entityId);
      } else {
        console.log('[CanvasApplication] Offline mode - HTTP auto-save only');
      }
      
      console.log('[CanvasApplication] ✅ Canvas loaded', {
        entityId,
        elements: this.yxml.length,
        hasExistingState: !!entity.skeleton?.base64,
        autoSaveEnabled: this.autoSaveEnabled,
        offline: options.offline
      });
      
    } catch (error) {
      console.error('[CanvasApplication] Failed to load:', error);
      throw error;
    }
  }
  
  /**
   * Initialize empty canvas
   */
  private initializeEmptyCanvas(): void {
    if (!this.yxml) return;
    
    // Canvas starts empty (no default elements)
    console.log('[CanvasApplication] Empty canvas initialized');
  }

  /**
   * Canvas-specific: Get elements by category
   */
  public getElementsByCategory(category: string): SerializedNode[] {
    const classNames = getClassesByCategory(category);
    const elements: SerializedNode[] = [];

    for (const className of classNames) {
      const nodeIds = this.byClassIndex.get(className);
      if (nodeIds) {
        for (const nodeId of nodeIds) {
          const node = this.byIdIndex.get(nodeId);
          if (node) elements.push(node as any);
        }
      }
    }

    return elements;
  }

  /**
   * Canvas-specific: Get all shapes
   */
  public getAllShapes(): SerializedNode[] {
    return this.getElementsByCategory('shape');
  }

  /**
   * Canvas-specific: Get all sticky notes
   */
  public getAllStickyNotes(): SerializedNode[] {
    const nodeIds = this.byClassIndex.get('sticky-note');
    if (!nodeIds) return [];

    return Array.from(nodeIds)
      .map(id => this.byIdIndex.get(id))
      .filter((node): node is any => node !== undefined);
  }

  /**
   * Canvas-specific: Get all text elements
   */
  public getAllTextElements(): SerializedNode[] {
    return this.getElementsByCategory('text');
  }

  /**
   * Canvas-specific: Get all connectors
   */
  public getAllConnectors(): SerializedNode[] {
    return this.getElementsByCategory('connector');
  }

  /**
   * Canvas-specific: Get elements in a spatial region
   */
  public getElementsInRegion(bounds: { x: number; y: number; width: number; height: number }): SerializedNode[] {
    const results: SerializedNode[] = [];

    // Use spatial index if available
    if (this.spatialIndex) {
      const query = {
        type: 'bounds' as const,
        bounds
      };
      const spatialNodes = this.spatialIndex.query(query);
      return spatialNodes.map(sn => this.byIdIndex.get(sn.id)!).filter(Boolean) as any;
    }

    // Fallback: iterate all nodes and check bounds
    for (const node of this.byIdIndex.values()) {
      const pos = node.attrs?.position;
      if (pos && pos.type === 'absolute') {
        const intersects = (
          pos.x < bounds.x + bounds.width &&
          pos.x + (pos.width || 0) > bounds.x &&
          pos.y < bounds.y + bounds.height &&
          pos.y + (pos.height || 0) > bounds.y
        );
        if (intersects) results.push(node as any);
      }
    }

    return results;
  }

  /**
   * Canvas-specific: Get canvas statistics
   */
  public getCanvasStats() {
    const allNodes = Array.from(this.byIdIndex.values());
    
    return {
      totalElements: allNodes.length,
      shapes: this.getAllShapes().length,
      stickyNotes: this.getAllStickyNotes().length,
      textElements: this.getAllTextElements().length,
      connectors: this.getAllConnectors().length,
      images: this.getNodesByClassName('canvas-image').length,
      groups: this.getNodesByClassName('canvas-group').length
    };
  }

  /**
   * Canvas-specific: Get canvas bounds (bounding box of all elements)
   */
  public getCanvasBounds(): { x: number; y: number; width: number; height: number } | null {
    const allNodes = Array.from(this.byIdIndex.values());
    
    if (allNodes.length === 0) return null;

    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;

    for (const node of allNodes) {
      const pos = node.attrs?.position;
      if (pos && pos.type === 'absolute') {
        minX = Math.min(minX, pos.x);
        minY = Math.min(minY, pos.y);
        maxX = Math.max(maxX, pos.x + (pos.width || 0));
        maxY = Math.max(maxY, pos.y + (pos.height || 0));
      }
    }

    if (minX === Infinity) return null;

    return {
      x: minX,
      y: minY,
      width: maxX - minX,
      height: maxY - minY
    };
  }

  /**
   * Canvas-specific: Export to SVG
   */
  public exportToSVG(): string {
    const bounds = this.getCanvasBounds();
    if (!bounds) return '<svg></svg>';

    let svg = `<svg width="${bounds.width}" height="${bounds.height}" xmlns="http://www.w3.org/2000/svg">`;
    
    // Add each element as SVG
    const allNodes = Array.from(this.byIdIndex.values());
    for (const node of allNodes) {
      const className = node.attrs?.className;
      const pos = node.attrs?.position;
      const style = node.attrs?.style;

      if (!pos || pos.type !== 'absolute') continue;

      // ✅ FIX: Convert canvas coords (Y-up) to SVG coords (Y-down)
      // Canvas uses mathematical Y-axis (up = positive), SVG uses screen Y-axis (down = positive)
      const svgX = pos.x - bounds.x;
      const svgY = bounds.height - (pos.y - bounds.y) - pos.height;

      // Basic SVG generation (simplified)
      if (className?.startsWith('shape-rectangle')) {
        svg += `<rect x="${svgX}" y="${svgY}" width="${pos.width}" height="${pos.height}" fill="${style?.fill || '#ccc'}" stroke="${style?.stroke || '#000'}" />`;
      } else if (className?.startsWith('shape-circle')) {
        const r = (pos.width || 0) / 2;
        // Circle center needs to be adjusted for Y-flip
        const circleCy = svgY + r;
        svg += `<circle cx="${svgX + r}" cy="${circleCy}" r="${r}" fill="${style?.fill || '#ccc'}" stroke="${style?.stroke || '#000'}" />`;
      }
      // Add more shape types as needed
    }

    svg += '</svg>';
    return svg;
  }

  /**
   * Canvas-specific: Search elements by text content
   */
  public searchElements(query: string): SerializedNode[] {
    const lowerQuery = query.toLowerCase();
    const results: SerializedNode[] = [];

    for (const node of this.byIdIndex.values()) {
      const text = this.extractTextFromNode(node as any);
      if (text.toLowerCase().includes(lowerQuery)) {
        results.push(node as any);
      }
    }

    return results;
  }

  /**
   * Helper: Extract text from node
   */
  private extractTextFromNode(node: SerializedNode): string {
    if (node.text) return node.text;
    if (node.content) {
      return node.content.map(child => this.extractTextFromNode(child)).join(' ');
    }
    return '';
  }

  /**
   * ✅ OVERRIDE: handleEntityRead for Canvas-specific features
   * Supports standard selectors + canvas-specific bounds selector for spatial queries
   */
  async handleEntityRead(selector: any, options: any = {}): Promise<any> {
    console.log('[CanvasApplication] 📖 handleEntityRead called');
    console.log('[CanvasApplication] Selector:', JSON.stringify(selector, null, 2));
    console.log('[CanvasApplication] Options:', JSON.stringify(options, null, 2));
    
    // ✅ CANVAS-SPECIFIC: Handle bounds selector for spatial queries
    if (selector.bounds) {
      console.log('[CanvasApplication] 🔍 Using spatial bounds query:', selector.bounds);
      
      // Use the existing getElementsInRegion method
      const elements = this.getElementsInRegion(selector.bounds);
      
      console.log('[CanvasApplication] ✅ Found', elements.length, 'elements in bounds');
      
      return {
        nodes: elements,
        wordCount: this.calculateWordCount(elements),
        stats: {
          nodeCount: elements.length,
          queryType: 'spatial-bounds'
        }
      };
    }
    
    // ✅ For all other selectors, use parent implementation
    // This handles: all, nodeId, nodeIds, className, query, containerId, range
    console.log('[CanvasApplication] 🔄 Delegating to BaseApplication for selector');
    return super.handleEntityRead(selector, options);
  }

  /**
   * Helper: Calculate bounding box for elements
   */
  private calculateBounds(elements: any[]) {
    if (elements.length === 0) {
      return { x: 0, y: 0, width: 0, height: 0 };
    }
    
    let minX = Infinity, minY = Infinity;
    let maxX = -Infinity, maxY = -Infinity;
    
    elements.forEach(el => {
      minX = Math.min(minX, el.x);
      minY = Math.min(minY, el.y);
      maxX = Math.max(maxX, el.x + el.width);
      maxY = Math.max(maxY, el.y + el.height);
    });
    
    return {
      x: minX,
      y: minY,
      width: maxX - minX,
      height: maxY - minY
    };
  }

  /**
   * ✅ IMPLEMENT: handleEntityWrite for AI integration
   * 
   * IMPORTANT: Canvas elements use PLAIN TEXT, not markdown.
   * Formatting is done via element-level style attributes (fontWeight, fontSize, etc.)
   * This prevents markdown delimiters like **bold** from appearing in canvas text.
   * 
   * ✅ CRITICAL FIX: Canvas uses Pure JSON format - UPDATE operations require special handling
   * to properly merge attributes into the 'data' JSON attribute.
   */
  async handleEntityWrite(params: {
    operation: 'insert' | 'update' | 'delete' | 'batch';
    content?: any;
    attrs?: any;
    target?: any;
    nodeId?: string;
    operations?: any[];
  }): Promise<any> {
    console.log('[CanvasApplication] ✏️ handleEntityWrite called:', { 
      operation: params.operation, 
      hasContent: !!params.content,
      hasAttrs: !!params.attrs,
      hasBatchOps: !!params.operations
    });
    
    if (!this.yxml || !this.ydoc) {
      throw new Error('Canvas not initialized');
    }

    // ✅ Handle BATCH operations specially (they contain multiple ops)
    if (params.operation === 'batch' && params.operations) {
      return this.handleBatchWrite(params);
    }

    // ✅ Handle UPDATE operations specially (Pure JSON format requires merging)
    if (params.operation === 'update') {
      return this.handleCanvasUpdate(params);
    }

    // ✅ CONSISTENCY FIX: Strip markdown delimiters from canvas text
    // Canvas elements use plain text with element-level styling (not inline markdown)
    if (params.content?.attrs?.text) {
      const original = params.content.attrs.text;
      params.content.attrs.text = this.stripMarkdownDelimiters(original);
      
      if (original !== params.content.attrs.text) {
        console.log('[CanvasApplication] 🧹 Stripped markdown delimiters:', {
          original: original.substring(0, 50),
          cleaned: params.content.attrs.text.substring(0, 50)
        });
      }
    }
    
    if (params.attrs?.text) {
      const original = params.attrs.text;
      params.attrs.text = this.stripMarkdownDelimiters(original);
      
      if (original !== params.attrs.text) {
        console.log('[CanvasApplication] 🧹 Stripped markdown delimiters from attrs:', {
          original: original.substring(0, 50),
          cleaned: params.attrs.text.substring(0, 50)
        });
      }
    }

    // For INSERT/REPLACE/DELETE, use parent implementation
    const result = await super.handleEntityWrite(params);
    
    console.log('[CanvasApplication] ✅ Operation completed:', params.operation);
    
    return result;
  }

  /**
   * Handle Canvas-specific UPDATE operations
   * 
   * Canvas uses Pure JSON format where ALL attributes are stored in a single 'data' JSON attribute.
   * UPDATE operations must:
   * 1. Read existing 'data' attribute
   * 2. Merge partial updates with existing data
   * 3. Validate merged data is still complete
   * 4. Write back complete 'data' attribute
   * 
   * ✅ FIX: Properly handles Y.js transactions and index rebuilding
   */
  private async handleCanvasUpdate(params: any): Promise<any> {
    const target = params.target || {};
    const attrs = params.attrs || {};
    
    console.log('[CanvasApplication] 🔧 Canvas UPDATE operation:', {
      target,
      attrsKeys: Object.keys(attrs)
    });
    
    // ✅ CRITICAL FIX: Rebuild indices BEFORE operations (like BaseApplication does)
    await this.buildCoreIndices();
    console.log('[CanvasApplication] ✅ Indices refreshed before UPDATE');
    
    // Find nodes to update
    const nodeIds = this.findNodesByTargetCanvas(target);
    
    if (nodeIds.length === 0) {
      console.warn('[CanvasApplication] ⚠️ No elements found matching target');
      return {
        success: false,
        error: 'No elements found matching target selector',
        operation: 'update',
        stats: { nodesModified: 0 }
      };
    }
    
    console.log('[CanvasApplication] 🔍 Found elements to update:', {
      count: nodeIds.length,
      nodeIds: nodeIds.slice(0, 5)
    });
    
    // Update each element using Pure JSON format
    const affectedIds: string[] = [];
    const errors: any[] = [];
    
    // ✅ CRITICAL FIX: Wrap all updates in a single Y.js transaction
    // This ensures atomicity, proper undo/redo, and collaboration sync
    this.ydoc!.transact(() => {
      for (const nodeId of nodeIds) {
        try {
          const index = this.findNodeIndexByIdCanvas(nodeId);
          
          if (index >= 0 && this.yxml) {
            const element = this.yxml.get(index) as Y.XmlElement;
            
            console.log('[CanvasApplication] 🔧 Updating element:', { nodeId, index });
            
            // Use Canvas-specific update logic (Pure JSON format)
            this.updateCanvasElement(element, attrs);
            
            affectedIds.push(nodeId);
            console.log('[CanvasApplication] ✅ Updated element:', nodeId);
          } else {
            console.warn('[CanvasApplication] ⚠️ Element index not found:', nodeId);
          }
        } catch (error: any) {
          console.error('[CanvasApplication] ❌ Failed to update element:', nodeId, error);
          errors.push({ nodeId, error: error.message });
          // Continue with other elements rather than failing completely
        }
      }
    });
    
    // ✅ CRITICAL FIX: Rebuild indices AFTER operations (like BaseApplication does)
    await this.buildCoreIndices();
    console.log('[CanvasApplication] ✅ Indices rebuilt after UPDATE');
    
    console.log('[CanvasApplication] ✅ UPDATE complete:', {
      succeeded: affectedIds.length,
      failed: errors.length,
      total: nodeIds.length
    });
    
    // Trigger auto-save
    await this.saveToBackend();
    
    return {
      success: affectedIds.length > 0,
      operation: 'update',
      stats: {
        nodesModified: affectedIds.length,
        nodesFailed: errors.length
      },
      errors: errors.length > 0 ? errors : undefined
    };
  }

  /**
   * Handle BATCH operations
   * Process multiple operations in a single atomic transaction
   * 
   * ✅ FIX: Wraps entire batch in one Y.js transaction for atomicity
   */
  private async handleBatchWrite(params: any): Promise<any> {
    const operations = params.operations || [];
    
    console.log('[CanvasApplication] 📦 BATCH operation:', {
      operationCount: operations.length
    });
    
    if (operations.length === 0) {
      return {
        success: false,
        error: 'No operations provided in batch',
        operation: 'batch',
        stats: { total: 0, succeeded: 0, failed: 0 }
      };
    }
    
    // ✅ CRITICAL FIX: Rebuild indices BEFORE batch operations
    await this.buildCoreIndices();
    console.log('[CanvasApplication] ✅ Indices refreshed before BATCH');
    
    const results: Array<{ success: boolean; operation?: string; nodesModified?: number; error?: string }> = [];
    let totalSucceeded = 0;
    let totalFailed = 0;
    
    // ✅ CRITICAL FIX: Wrap entire batch in ONE transaction for atomicity
    // ✅ FIX: Use 'canvas-batch-write' as origin instead of clientID
    // This signals to React observer to ALWAYS reload state for batch operations
    // (otherwise observer skips "local" changes and React never updates)
    this.ydoc!.transact(() => {
      for (let i = 0; i < operations.length; i++) {
        const op = operations[i];
        
        // Convert batch op format to standard format
        const operation = op.op || op.operation;
        const target = op.target;
        const content = op.content;
        const attrs = op.attrs;
        
        console.log('[CanvasApplication] 📦 Batch op', i + 1, '/', operations.length, ':', operation);
        
        try {
          // Handle UPDATE operations directly (can't call async methods in transaction)
          if (operation === 'update') {
            const nodeIds = this.findNodesByTargetCanvas(target);
            const affectedIds: string[] = [];
            
            for (const nodeId of nodeIds) {
              const index = this.findNodeIndexByIdCanvas(nodeId);
              if (index >= 0 && this.yxml) {
                const element = this.yxml.get(index) as Y.XmlElement;
                this.updateCanvasElement(element, attrs);
                affectedIds.push(nodeId);
              }
            }
            
            results.push({ 
              success: affectedIds.length > 0,
              operation: 'update',
              nodesModified: affectedIds.length 
            });
            
            if (affectedIds.length > 0) {
              totalSucceeded++;
            } else {
              totalFailed++;
            }
          }
          // ✅ INSERT: Delegate to BaseApplication's synchronous executeInsert
          else if (operation === 'insert') {
            if (!content) {
              results.push({
                success: false,
                operation: 'insert',
                error: 'Missing content for insert operation'
              });
              totalFailed++;
              continue;
            }
            
            const params = { content, target, operation: 'insert' };
            const affectedIds = this.executeInsert(params);
            
            results.push({
              success: affectedIds.length > 0,
              operation: 'insert',
              nodesModified: affectedIds.length
            });
            
            if (affectedIds.length > 0) {
              totalSucceeded++;
            } else {
              totalFailed++;
            }
          }
          // ✅ DELETE: Delegate to BaseApplication's synchronous executeDelete (skip nested transaction)
          else if (operation === 'delete' || operation === 'remove') {
            if (!target) {
              results.push({
                success: false,
                operation: 'delete',
                error: 'Missing target for delete operation'
              });
              totalFailed++;
              continue;
            }
            
            const params = { target, operation: 'delete' };
            const affectedIds = this.executeDelete(params, true);  // skipTransaction=true
            
            results.push({
              success: affectedIds.length > 0,
              operation: 'delete',
              nodesModified: affectedIds.length
            });
            
            if (affectedIds.length > 0) {
              totalSucceeded++;
            } else {
              totalFailed++;
            }
          }
          // ✅ REPLACE: Delegate to BaseApplication's synchronous executeReplace
          else if (operation === 'replace') {
            if (!content) {
              results.push({
                success: false,
                operation: 'replace',
                error: 'Missing content for replace operation'
              });
              totalFailed++;
              continue;
            }
            
            const params = { content, target, operation: 'replace' };
            const affectedIds = this.executeReplace(params);
            
            results.push({
              success: affectedIds.length > 0,
              operation: 'replace',
              nodesModified: affectedIds.length
            });
            
            if (affectedIds.length > 0) {
              totalSucceeded++;
            } else {
              totalFailed++;
            }
          }
          // Unknown operation type
          else {
            console.warn('[CanvasApplication] ⚠️ Unknown batch operation type:', operation);
            results.push({ 
              success: false, 
              error: `Unknown batch operation type '${operation}'`
            });
            totalFailed++;
          }
        } catch (error: any) {
          console.error('[CanvasApplication] ❌ Batch op', i + 1, 'failed:', error);
          results.push({ success: false, error: error.message });
          totalFailed++;
        }
      }
    }, 'canvas-batch-write');  // ✅ Custom origin to force React reload
    
    // ✅ CRITICAL FIX: Rebuild indices AFTER batch operations
    await this.buildCoreIndices();
    console.log('[CanvasApplication] ✅ Indices rebuilt after BATCH');
    
    console.log('[CanvasApplication] ✅ BATCH complete:', {
      total: operations.length,
      succeeded: totalSucceeded,
      failed: totalFailed
    });
    
    // Trigger auto-save
    await this.saveToBackend();
    
    return {
      success: totalSucceeded > 0,
      operation: 'batch',
      stats: {
        total: operations.length,
        succeeded: totalSucceeded,
        failed: totalFailed
      },
      results
    };
  }

  /**
   * Update a Canvas element in Pure JSON format
   * 
   * Reads current 'data' attribute, merges partial updates, validates, and writes back.
   */
  private updateCanvasElement(element: Y.XmlElement, attrs: any): void {
    // 1. Read current data (Pure JSON format)
    const dataAttr = element.getAttribute('data');
    
    if (!dataAttr) {
      console.error('[CanvasApplication] ❌ Element missing data attribute');
      throw new Error('Cannot update element: missing data attribute (element may be in old format)');
    }
    
    let currentData: any;
    try {
      currentData = JSON.parse(dataAttr);
      console.log('[CanvasApplication] 📖 Current data:', {
        id: currentData.id,
        type: currentData.type,
        hasPosition: !!(currentData.x !== undefined && currentData.y !== undefined),
        dataKeys: Object.keys(currentData)
      });
    } catch (e) {
      console.error('[CanvasApplication] ❌ Failed to parse data attribute:', e);
      throw new Error('Cannot update element: corrupt data attribute');
    }
    
    // 2. Merge updates into current data
    const updatedData = this.mergeCanvasAttributes(currentData, attrs);
    
    console.log('[CanvasApplication] 🔀 Merged data:', {
      id: updatedData.id,
      updatedFields: Object.keys(attrs),
      hasPosition: !!(updatedData.x !== undefined && updatedData.y !== undefined)
    });
    
    // 3. Validate updated data is still complete
    if (!this.validateCanvasData(updatedData)) {
      console.error('[CanvasApplication] ❌ Update would create invalid element:', updatedData);
      throw new Error('Update validation failed: merged data is missing required fields');
    }
    
    console.log('[CanvasApplication] ✅ Validation passed');
    
    // 4. Write back complete data
    const newDataStr = JSON.stringify(updatedData);
    element.setAttribute('data', newDataStr);
    
    console.log('[CanvasApplication] 💾 Data written back:', {
      id: updatedData.id,
      dataLength: newDataStr.length
    });
  }

  /**
   * Merge partial attribute updates with existing Canvas element data
   * 
   * Handles special cases:
   * - position: Flattens into x, y, width, height
   * - style: Deep merge
   * - semantic: Deep merge
   * - start/end: For lines/arrows
   */
  private mergeCanvasAttributes(currentData: any, attrs: any): any {
    const merged = { ...currentData };
    
    // Handle position updates (special case - flatten into x, y, width, height)
    if (attrs.position) {
      const pos = attrs.position;
      if (pos.x !== undefined) merged.x = pos.x;
      if (pos.y !== undefined) merged.y = pos.y;
      if (pos.width !== undefined) merged.width = pos.width;
      if (pos.height !== undefined) merged.height = pos.height;
      
      console.log('[CanvasApplication] 🔀 Merged position:', {
        before: { x: currentData.x, y: currentData.y, width: currentData.width, height: currentData.height },
        updates: pos,
        after: { x: merged.x, y: merged.y, width: merged.width, height: merged.height }
      });
    }
    
    // Handle style updates (deep merge - preserve existing style properties)
    if (attrs.style) {
      merged.style = {
        ...(merged.style || {}),
        ...attrs.style
      };
      
      console.log('[CanvasApplication] 🔀 Merged style:', {
        before: currentData.style,
        updates: attrs.style,
        after: merged.style
      });
    }
    
    // Handle text updates (strip markdown delimiters)
    if (attrs.text !== undefined) {
      merged.text = this.stripMarkdownDelimiters(attrs.text);
      
      console.log('[CanvasApplication] 🔀 Updated text:', {
        before: currentData.text?.substring(0, 50),
        after: merged.text?.substring(0, 50)
      });
    }
    
    // Handle semantic updates (deep merge)
    if (attrs.semantic) {
      merged.semantic = {
        ...(merged.semantic || {}),
        ...attrs.semantic
      };
      
      console.log('[CanvasApplication] 🔀 Merged semantic:', merged.semantic);
    }
    
    // Handle start/end for lines/arrows (absolute coordinates)
    if (attrs.start) {
      merged.start = attrs.start;
      console.log('[CanvasApplication] 🔀 Updated start:', attrs.start);
    }
    if (attrs.end) {
      merged.end = attrs.end;
      console.log('[CanvasApplication] 🔀 Updated end:', attrs.end);
    }
    
    // Handle any other flat attributes (not position, style, text, semantic, start, end)
    Object.keys(attrs).forEach(key => {
      if (!['position', 'style', 'text', 'semantic', 'start', 'end'].includes(key)) {
        merged[key] = attrs[key];
        console.log('[CanvasApplication] 🔀 Updated', key + ':', attrs[key]);
      }
    });
    
    return merged;
  }

  /**
   * Validate Canvas element data has all required fields
   * 
   * Requirements:
   * - Must have id and type
   * - Regular elements: Must have x, y, width, height (numbers, positive)
   * - Lines/arrows: Must have start and end points (with x, y coordinates)
   * 
   * ✅ FIX: Added validation for positive sizes and canvas bounds checking
   */
  private validateCanvasData(data: any): boolean {
    // Must have id and type
    if (!data.id || !data.type) {
      console.error('[CanvasApplication] ❌ Validation failed: missing id or type');
      return false;
    }
    
    // Lines/arrows can use start/end instead of x/y
    const isLineOrArrow = data.type === 'line' || data.type === 'arrow';
    
    if (isLineOrArrow) {
      // Must have start and end points
      if (!data.start || !data.end) {
        console.error('[CanvasApplication] ❌ Validation failed: line/arrow missing start or end');
        return false;
      }
      
      // Validate start point
      if (typeof data.start.x !== 'number' || typeof data.start.y !== 'number') {
        console.error('[CanvasApplication] ❌ Validation failed: invalid start point', data.start);
        return false;
      }
      
      // Validate end point
      if (typeof data.end.x !== 'number' || typeof data.end.y !== 'number') {
        console.error('[CanvasApplication] ❌ Validation failed: invalid end point', data.end);
        return false;
      }
      
      // Check for NaN/Infinity
      if (isNaN(data.start.x) || isNaN(data.start.y) || !isFinite(data.start.x) || !isFinite(data.start.y)) {
        console.error('[CanvasApplication] ❌ Validation failed: start point is NaN or Infinity');
        return false;
      }
      if (isNaN(data.end.x) || isNaN(data.end.y) || !isFinite(data.end.x) || !isFinite(data.end.y)) {
        console.error('[CanvasApplication] ❌ Validation failed: end point is NaN or Infinity');
        return false;
      }
      
      // ✅ NEW: Warn if coordinates outside canvas bounds (don't fail, just warn)
      const CANVAS_BOUNDS = 2500;
      if (Math.abs(data.start.x) > CANVAS_BOUNDS || Math.abs(data.start.y) > CANVAS_BOUNDS ||
          Math.abs(data.end.x) > CANVAS_BOUNDS || Math.abs(data.end.y) > CANVAS_BOUNDS) {
        console.warn('[CanvasApplication] ⚠️ Line/arrow coordinates outside canvas bounds:', {
          start: data.start,
          end: data.end,
          bounds: `±${CANVAS_BOUNDS}`
        });
        // Continue - don't fail validation, element can still be created
      }
    } else {
      // Regular elements must have x, y, width, height
      if (typeof data.x !== 'number' || typeof data.y !== 'number') {
        console.error('[CanvasApplication] ❌ Validation failed: missing or invalid x/y', {
          x: data.x,
          y: data.y,
          xType: typeof data.x,
          yType: typeof data.y
        });
        return false;
      }
      
      if (typeof data.width !== 'number' || typeof data.height !== 'number') {
        console.error('[CanvasApplication] ❌ Validation failed: missing or invalid width/height', {
          width: data.width,
          height: data.height
        });
        return false;
      }
      
      // ✅ NEW: Validate width and height are positive
      if (data.width <= 0 || data.height <= 0) {
        console.error('[CanvasApplication] ❌ Validation failed: width/height must be positive', {
          width: data.width,
          height: data.height
        });
        return false;
      }
      
      // Check for NaN/Infinity
      if (isNaN(data.x) || isNaN(data.y) || !isFinite(data.x) || !isFinite(data.y)) {
        console.error('[CanvasApplication] ❌ Validation failed: position is NaN or Infinity');
        return false;
      }
      if (isNaN(data.width) || isNaN(data.height) || !isFinite(data.width) || !isFinite(data.height)) {
        console.error('[CanvasApplication] ❌ Validation failed: size is NaN or Infinity');
        return false;
      }
      
      // ✅ NEW: Warn if coordinates outside canvas bounds (don't fail, just warn)
      const CANVAS_BOUNDS = 2500;
      if (Math.abs(data.x) > CANVAS_BOUNDS || Math.abs(data.y) > CANVAS_BOUNDS) {
        console.warn('[CanvasApplication] ⚠️ Element position outside canvas bounds:', {
          x: data.x,
          y: data.y,
          bounds: `±${CANVAS_BOUNDS}`
        });
        // Continue - don't fail validation, element can still be created
      }
    }
    
    console.log('[CanvasApplication] ✅ Validation passed for:', data.id);
    return true;
  }

  /**
   * Helper: Find nodes by target selector (delegates to parent)
   * 
   * Note: Must be protected (not private) to avoid TypeScript conflict with BaseApplication
   */
  protected findNodesByTargetCanvas(target: any): string[] {
    // Use IndexManager methods from parent
    if (target.nodeId) {
      return [target.nodeId];
    }
    if (target.nodeIds) {
      return target.nodeIds;
    }
    if (target.className) {
      const nodeIds = this.byClassIndex.get(target.className);
      return nodeIds ? Array.from(nodeIds) : [];
    }
    if (target.bounds) {
      const elements = this.getElementsInRegion(target.bounds);
      return elements.map(el => el.id).filter(Boolean);
    }
    if (target.all === true) {
      return Array.from(this.indexManager.getAllIds());
    }
    return [];
  }

  /**
   * Helper: Find node index by ID
   * 
   * Note: Must be protected (not private) to avoid TypeScript conflict with BaseApplication
   */
  protected findNodeIndexByIdCanvas(nodeId: string): number {
    if (!this.yxml) return -1;
    
    for (let i = 0; i < this.yxml.length; i++) {
      const child = this.yxml.get(i);
      if (child instanceof Y.XmlElement) {
        const id = child.getAttribute('id');
        if (id === nodeId) {
          return i;
        }
      }
    }
    
    return -1;
  }

  /**
   * Strip markdown delimiters from text
   * Canvas elements use plain text with element-level styling
   * 
   * Examples:
   *   "**bold**" → "bold"
   *   "*italic*" → "italic"
   *   "`code`" → "code"
   *   "# Heading" → "Heading"
   */
  private stripMarkdownDelimiters(text: string): string {
    if (!text) return text;
    
    return text
      // Bold + italic (must be first to avoid partial matches)
      .replace(/\*\*\*(.+?)\*\*\*/g, '$1')  // ***bold+italic*** → text
      // Bold
      .replace(/\*\*(.+?)\*\*/g, '$1')      // **bold** → bold
      // Italic
      .replace(/\*(.+?)\*/g, '$1')          // *italic* → italic
      // Code
      .replace(/`(.+?)`/g, '$1')            // `code` → code
      // Headings (remove leading #'s and space)
      .replace(/^#{1,6}\s+/gm, '')          // # heading → heading
      // List markers (remove leading - or * and space)
      .replace(/^[-*]\s+/gm, '');           // - item → item
  }
  
  /**
   * ✅ FIX (Issue #3): Override destroy to clean up Canvas-specific resources
   * This prevents memory leaks by properly destroying the UndoManager
   */
  public override async destroy(): Promise<void> {
    console.log('[CanvasApplication] Destroying canvas...');
    
    // ✅ Clean up Canvas-specific UndoManager FIRST
    if (this.undoManager) {
      try {
        this.undoManager.destroy();
        this.undoManager = null;
        console.log('[CanvasApplication] ✅ UndoManager destroyed');
      } catch (error) {
        console.error('[CanvasApplication] ❌ Failed to destroy UndoManager:', error);
      }
    }
    
    // ✅ Call parent destroy (handles Y.Doc save, persistence cleanup, collaboration cleanup, etc.)
    await super.destroy();
    
    console.log('[CanvasApplication] ✅ Canvas destroy complete');
  }
}

